// ────────────────────────────────────────────────────────────────────────────
// The accounting a charge line defaults to on an invoice — its sales nominal
// and its accounting category — and the products it prints.
//
//   nominal:   the charge definition's sales nominal.
//   category:  charge's category follows the job → product → cargo → charge;
//              it does not                       → charge.
//   products:  only on lines that follow the job (the transport and its fuel
//              surcharge) — waiting time does not restate the goods.
//
// Products on one order that disagree on their category take no product
// category (falls to the cargo / charge) and flag the line, as for VAT.
// ────────────────────────────────────────────────────────────────────────────
import type { CargoDefinition, ChargeCostDefinition, ChargeCostTypeValue, OrderInvoiceProduct } from '@my/tms-api';

export type LineCategorySource = 'product' | 'cargo' | 'charge';

export interface LineCategoryResolution {
  /** Upper-cased category CODE, or null when nothing on the chain has one. */
  code: string | null;
  source: LineCategorySource | null;
  productsDisagree: boolean;
}

export function resolveLineCategory(args: {
  charge: ChargeCostDefinition | undefined;
  cargo: CargoDefinition | undefined;
  productIds: readonly string[];
}): LineCategoryResolution {
  const { charge, cargo, productIds } = args;
  const norm = (c: string | null | undefined) => (c?.trim() ? c.trim().toUpperCase() : null);
  const chargeCode = norm(charge?.accountingCategoryCode);
  const fallback: LineCategoryResolution = {
    code: chargeCode,
    source: chargeCode ? 'charge' : null,
    productsDisagree: false,
  };
  if (!charge?.categoryFollowsJob) return fallback;

  const cargoCode = norm(cargo?.accountingCategoryCode);
  const links = new Map((cargo?.products ?? []).map((p) => [p.productId, p]));
  const effective = productIds.map((id) => {
    const own = norm(links.get(id)?.accountingCategoryCode);
    return { own, code: own ?? cargoCode };
  });
  let productsDisagree = false;
  if (effective.some((e) => e.own)) {
    const codes = new Set(effective.map((e) => e.code ?? ''));
    const first = effective[0]?.code;
    if (codes.size === 1 && first) return { code: first, source: 'product', productsDisagree: false };
    productsDisagree = true;
  }
  if (cargoCode) return { code: cargoCode, source: 'cargo', productsDisagree };
  return { ...fallback, productsDisagree };
}

/** Whether a line restates the goods: charges that follow the job, or the transport itself. */
export function lineCarriesProducts(
  charge: ChargeCostDefinition | undefined,
  type: ChargeCostTypeValue,
): boolean {
  return charge ? charge.categoryFollowsJob : type === 'TransportCharge';
}

/** "Sulphuric Acid (UN 1830), Caustic Soda" — what a line prints. Null when none. */
export function productsText(products: readonly OrderInvoiceProduct[]): string | null {
  if (products.length === 0) return null;
  const text = products
    .map((p) => (p.unNumber ? `${p.name} (UN ${p.unNumber})` : p.name))
    .join(', ');
  return text.length > 512 ? `${text.slice(0, 509)}...` : text;
}
